<template>
  <div class="ConfigureChannels paddedPage">
    <div style="display: flex; align-items: center; gap: 10px;">
      <h3>
        Configure channels
      </h3>
      <Dropdown v-model="showingBins"
        :options="showingBinsOptions"
        :optionLabel="'label'"
        :optionValue="'value'"
      ></Dropdown>
      <Dropdown v-model="selectedGroup"
        :options="selectedGroupOptions"
        :optionLabel="'label'"
        :optionValue="'value'"
      ></Dropdown>
      <Button v-if="showingBins" severity="secondary" @click="showingEditBinsDialog = true">
        <i class="pi pi-cog"></i>
        Edit bins
      </Button>
      <div style="flex-grow: 1;"></div>
      <template v-if="showSubmit">
        <Button size="small" @click="submit()">
          <i class="pi pi-check"></i>
          Submit
        </Button>
        <Button size="small" severity="danger" @click="cancel()">
          <i class="pi pi-times"></i>
          Cancel
        </Button>
      </template>
    </div>
    <EditBinsDialog v-model:visible="showingEditBinsDialog"></EditBinsDialog>
    <MultiColumnTable :tableData="tableData"></MultiColumnTable>
  </div>
</template>

<script>
import { computed, ref, reactive, watchEffect, onUnmounted } from "vue";
import { last, map_dropNulls, set } from "./utility.js";
import { getBoardHostname, createChannelMap, isAssigned, getBoardId } from "./appUtility.js";
import { resolveTableTemplate } from "./TableUtility.js";
import EditBinsDialog from "./EditBinsDialog.vue";
import MultiColumnTable from "./MultiColumnTable.vue";
import ConfigureChannelsBinHeader from "./ChannelAssignmentsBinHeader.vue";
import store from "./store.js";
import Tag from "./Tag.js";
import Session from "./Session.js";

export let systemCopy = ref(null);
export let showSubmit = ref(false);

export default {
  components: { EditBinsDialog, MultiColumnTable, ConfigureChannelsBinHeader },
  data() {
    return { 
      store,
      showSubmit,
      showingBins: true,
      selectedGroup: "analogInputs",
      showingEditBinsDialog: false,
      binsMode: "bins",
      editMode: "analogInputs",
      editingBin: [],
      submitPending: false,
      editingControllers: false,
      firstRender: true
    };
  },
  setup(props) {
    // Create temporary sessions connected to boards we aren't already connected to.
    // Boards not configured to appear on the status/settings pages are the boards requiring temporary sessions for as long as this page is open.
    let temporarySessions = ref([]);
    onUnmounted(() => temporarySessions.value.forEach(session => session.close()));
    
    watchEffect(() => {
      let primarySession = store.sessions[0];
      let boards = primarySession.system.boardsOnSite.children;
      let boardsRequiringTemporarySessions = boards.filter(board =>
        board.online.value &&
        board.programName.value == "mvv2" &&
        board.programCommit.value == primarySession.system.programCommit.value &&
        board.showUi.value == false
      );
      
      let hostnames = boardsRequiringTemporarySessions.map(getBoardHostname);
      let newHostnames = hostnames.filter(hostname => !temporarySessions.value.find(session => hostname == session.hostname));
      let newSessions = newHostnames.map(hostname => reactive(new Session(hostname)));
      newSessions.forEach(session => session.loadApp(() => session.pollApp(null, 500)));

      let expiredSessions = temporarySessions.value.filter(session => !hostnames.includes(session.hostname));
      expiredSessions.forEach(session => session.close());

      temporarySessions.value = temporarySessions.value.filter(session => !expiredSessions.includes(session));
      temporarySessions.value.push(... newSessions);
    });

    let sessions = computed(() => [ ... store.sessions, ... temporarySessions.value ]);
    return {
      sessions
    };
  },
  computed: {
    showingBinsOptions() {
      return [
        { label: "System", value: false },
        { label: "Bins", value: true }
      ];
    },
    selectedGroupOptions() {
      let options = [
        { label: "Analog inputs", value: "analogInputs" },
        { label: "Analog outputs", value: "analogOutputs", disabled: !this.showingBins },
        { label: "Digital inputs", value: "digitalInputs" },
        { label: "Digital outputs", value: "digitalOutputs" }
      ];
      return options.filter(option => !option.disabled);
    },
    tabs() {
      let tabs = [
        { label: "System", modeKey: "binsMode", modeValue: "system", relevant: true },
        { label: "Bins", modeKey: "binsMode", modeValue: "bins", relevant: true },
        { label: "Analog inputs", modeKey: "editMode", modeValue: "analogInputs", relevant: true },
        { label: "Analog outputs", modeKey: "editMode", modeValue: "analogOutputs", relevant: true, disabled: this.binsMode == "system" },
        { label: "Digital inputs", modeKey: "editMode", modeValue: "digitalInputs", relevant: true },
        { label: "Digital outputs", modeKey: "editMode", modeValue: "digitalOutputs", relevant: true },
        // { label: "Controllers", modeKey: "editMode", modeValue: "controllers", relevant: this.binsMode == "system" },
        // { label: "Edit bins", modeKey: "editMode", modeValue: "bins", relevant: this.binsMode == "bins" }
      ];
      return tabs.map(tab => {
        return {
          ... tab,
          active: this[tab.modeKey] == tab.modeValue,
          handler: () => {
            this[tab.modeKey] = tab.modeValue;
            this.firstRender = true;
            setTimeout(() => this.firstRender = false);
          }
        }
      });
    },
    tableData() {
      let map = {
        system: {
          analogInputs: "systemAnalogInputs",
          analogOutputs: null, 
          digitalInputs: "systemDigitalInputs",
          digitalOutputs: "systemDigitalOutputs"
        },
        bins: {
          analogInputs: "binAnalogInputs",
          analogOutputs: "binAnalogOutputs",
          digitalInputs: "binDigitalInputs",
          digitalOutputs: "binDigitalOutputs"
        }
      };
      // Add the 'controllers' and 'channelMap' props to each cellComponent contained within the selected table-templates.
      let session = this.sessions[0];
      let templateKey = map[this.showingBins ? 'bins' : 'system'][this.selectedGroup];
      let tableTemplate = session.system.tables[templateKey].value;
      let augmentedTemplate = {
        ...tableTemplate,
        rowTemplates: tableTemplate.rowTemplates.map(rowTemplate => {
          if (rowTemplate.cellComponentType == "ChannelAssignmentsSetting") {
            return {
              ...rowTemplate,
              cellComponentArgs: {
                ...rowTemplate.cellComponentArgs,
                controllers: this.controllers,
                channelMap: this.channelMap
              }
            };
          }
          else {
            return rowTemplate;
          }
        })
      };
      return resolveTableTemplate(session, systemCopy.value, augmentedTemplate);
    },
    controllers() {
      let boards = this.sessions[0].system.boardsOnSite.children;
      let controllers = map_dropNulls(this.sessions, session => session.system?.controller);
      return boards.map(board => {
        let controller = controllers.find(controller => {
          if (board.boardId.value == "localhost") return store.sessions[0].system.controller;
          else return board.boardId.value == getBoardId(controller.macAddress.value);
        });
        return {
          online: (controller != null),
          boardId: board.boardId.value,
          boardName: board.boardName.value,
          macAddress: board.macAddress.value,
          autoConfigureEnabled: controller?.autoConfigureEnabled.value,
          ports: controller?.ports.children,
          sensorModels: controller?.sensorModels.children
        };
      });
    }, 
    channelMap() {
      return createChannelMap(this.sessions, systemCopy.value);
    }
  },
  watch: {
    showingBins() {
      if (!this.showingBins && this.selectedGroup == "analogOutputs")
        this.selectedGroup = "analogInputs";
    }
  },
  created() {
    this.cloneSystem();
    this.$router.beforeEach(() => {
      if (this.showSubmit) {
        alert("Submit or cancel changes");
        return false;
      }
    });
  },
  mounted() {
    setTimeout(() => this.firstRender = false);
  },
  methods: {
    cancel() {
      showSubmit.value = false;
      this.cloneSystem();
    },
    submit() {
      showSubmit.value = false;
      this.submitPending = true;

      let session = this.sessions[0];
      let buttons = [ ... document.querySelectorAll(".channelPickerButton") ];
      buttons.forEach(b => b.setAttribute("disabled", true));
      let serializedSystem = serializeSystem(systemCopy.value, session.controlPaths);
      session.queueRequest("/cmd/changeIoConfig", serializedSystem, () => {
        this.submitPending = false;
        buttons.forEach(b => b.removeAttribute("disabled"));
      });
      session.pollApp(() => {
        this.cloneSystem();
      });
    },
    addBin() {
      let session = this.sessions[0];
      let bin = new Tag(session, systemCopy.value.bins, session.system.tagJsonTemplates.value.bin);
      bin.name.value= `Bin ${systemCopy.value.bins.children.length + 1}`;
      systemCopy.value.bins.children.push(bin);
      showSubmit.value = true;
      updatePlaceholders();
    },
    cloneSystem() {
      systemCopy.value = this.sessions[0].system.$clone();
      updatePlaceholders();
    }
  }
};

// Serializes any tags in 'system' that are referenced by 'controlPaths'.
// This is done, instead of just serializing and submitting everything, so that 
// overloads of "nlohmann::from_json()" needn't be written for tags that don't otherwise 
// need them, and so that stale values for tags unrelated to the io-config are not submitted.
function serializeSystem(system, controlPaths) {
  let json = {};
  
  let containerPaths = new Set();
  controlPaths.forEach(controlPath => {
    let containerPath = "";
    let tokens = controlPath.split("[]").slice(0, -1);
    tokens.forEach(token => {
      containerPaths.add(containerPath + token);
      containerPath += (token + "[]");
    });
  })

  let clone = system.$clone();
  clone.bins.children.forEach(bin => {
    let slice = function (container) { container.children = container.children.slice(0, -1); }
    slice(bin.auxiliaries); // TODO: Eliminate need to manually slice these containers.
    slice(bin.refrigerationUnits);
    slice(bin.heatingUnits);
  });

  containerPaths.forEach(path => {
    clone.$atPath(path, true).forEach(container => {
      if (container.children.length == 0)
        set(json, container.$path(), []);
      else
        container.children.forEach(child => {
          set(json, child.$path(), { 
            handle: parseInt(child.handle), 
            name: child.name?.value.trim()
          });
        });
    });
  });

  controlPaths.forEach(path => {
    clone.$atPath(path, true).forEach(control => {
      set(json, control.$path(), {
        handle: parseInt(control.handle),
        name: control.name?.value.trim(),
        boardId: control.boardId.value,
        boardName: control.boardName.value,
        portNumber: control.portNumber.value,
        channelNumber: control.channelNumber.value,
      });
    });
  });
  
  return json;
}

export function updatePlaceholders() {
  let templates = systemCopy.value.tagJsonTemplates.value;
  _updatePlaceholders("bins[].auxiliaries", templates.auxiliary);
  _updatePlaceholders("bins[].refrigerationUnits", templates.refrigerationUnit);
  _updatePlaceholders("bins[].heatingUnits", templates.heatingUnit);
}

function _updatePlaceholders(path, template) {
  let arrayTags = systemCopy.value.$atPath(path);

  // Remove unassigned children.
  arrayTags.forEach(arrayTag => {
    arrayTag.children = arrayTag.children.filter(child => {
      return getControls(child).some(isAssigned);
    });
  });

  let multipleInAnyBin = arrayTags.some(arrayTag => {
    return arrayTag.children.length > 1;
  });
    
  arrayTags.forEach(arrayTag => {
    // Add a placeholder if appropriate.
    let children = arrayTag.children;
    let lastChild = last(children);
    let defaultName_single = arrayTag.defaultName_single ?? arrayTag.defaultName_multiple;
    let defaultName_multiple = arrayTag.defaultName_multiple;
    if (!lastChild || getControls(lastChild).some(isAssigned)) {
      let child = new Tag(store.sessions[0], arrayTag, template);
      child.name.value = (lastChild || multipleInAnyBin) ? defaultName_multiple : defaultName_single;
      child.name.value = child.name.value.replace("{#}", children.length + 1);
      children.push(child);
    }
    // Rename children according to their index where appropriate.
    renameChildren(arrayTag.children, 0, multipleInAnyBin, defaultName_single, defaultName_multiple);
  });
}

function getControls(tag) {
  if (tag == null) 
    return [];
    
  let controls = [];
  let tags = [ tag ];
  while (tags.length) {
    tags.forEach(tag => {
      if (tag.channelNumber)
        controls.push(tag);
    });
    tags = tags.flatMap(tag => tag.$children());
  }
  return controls;
}

export function renameChildren(children, fromIndex, multipleInAnyBin, defaultName_single, defaultName_multiple) {
  for (let index = fromIndex; index < children.length; index++) {
    let child = children[index];
    let name = child.name;
    if (index == 0) {
      let renameAsSingle = !multipleInAnyBin && (
        name.value == "" ||
        name.value == defaultName_multiple.replace("{#}", "1") || 
        name.value == defaultName_multiple.replace("{#}", "2")
      );
      let renameAsMultiple = multipleInAnyBin && (
        name.value == "" ||
        name.value == defaultName_single ||
        name.value == defaultName_multiple.replace("{#}", "2")
      );
      if (renameAsSingle)
        name.value = defaultName_single.replace("{#}", "1");
      else if (renameAsMultiple)
        name.value = defaultName_multiple.replace("{#}", "1");
    }
    else {
      let renameAsMultiple = (
        name.value == "" ||
        name.value == defaultName_multiple.replace("{#}", index + 2)
      );
      if (renameAsMultiple)
        name.value = defaultName_multiple.replace("{#}", index + 1);
    }
  };
}
</script>

<style>
.ConfigureChannels {
  padding-top: 10px;
}
.configure-channels--title {
  white-space: nowrap;
}
#app .configure-channels--tabs {
  display: flex;
  align-items: center;
  gap: 25px;
  padding: 5px;
  flex-shrink: 0;
}
#app .configure-channels--tabs button:disabled {
  opacity: 0.3;
}
#app .configure-channels--submit {
  color: white;
  background: rgb(44, 180, 16);
}
#app .configure-channels--cancel {
  color: white;
  background: red;
}
#app .bin-button,
#app .configure-channels .edit-bin > button {
  color: white;
  padding: 0;
  padding-left: 10px;
  padding-right: 10px;
  height: 25px;
  line-height: 15px;
  margin-top: 5px;
  margin-bottom: 5px;
  background: slategrey;
}

.configure-channels--bin-name .dropdown-menu {
  margin-top: 10px;
}

@media (max-width: 1200px) {
  .configure-channels.showing-submit .configure-channels--title {
    display: none;
  }
}
</style>

